import React, { forwardRef } from "react";
import { type BoxProps } from "./types";
export * from "./types";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";

// Simple mapping for spacing tokens to px values
const SPACING: Record<string, number> = {
  xs: 4,
  s: 8,
  m: 16,
  l: 24,
  xl: 32,
};

const getSpace = (val?: string | number) => {
  if (typeof val === "number") return `${val}px`;
  if (typeof val === "string" && val in SPACING) return `${SPACING[val]}px`;
  return undefined;
};

export const Box = forwardRef<HTMLElement, BoxProps>(
  (
    {
      as: Component = "div",
      children,

      flexDirection,
      justifyContent,
      alignItems,
      flexWrap,
      flex,
      gap,

      m,
      mt,
      mr,
      mb,
      ml,
      mx,
      my,
      p,
      pt,
      pr,
      pb,
      pl,
      px,
      py,

      bg,
      borderRadius,
      width,
      height,
      borderColor,
      borderWidth,
      disabled,

      className,
      style,
      ...props
    },
    ref,
  ) => {
    const { theme } = useTheme(); // Replaced hardcoded theme with useTheme hook

    const styles: React.CSSProperties = React.useMemo(
      () => ({
        display: "flex",
        flexDirection,
        justifyContent,
        alignItems,
        flexWrap,
        flex,
        gap: getSpace(gap),

        margin: getSpace(m),
        marginTop: getSpace(mt ?? my),
        marginRight: getSpace(mr ?? mx),
        marginBottom: getSpace(mb ?? my),
        marginLeft: getSpace(ml ?? mx),

        padding: getSpace(p),
        paddingTop: getSpace(pt ?? py),
        paddingRight: getSpace(pr ?? px),
        paddingBottom: getSpace(pb ?? py),
        paddingLeft: getSpace(pl ?? px),

        backgroundColor: resolveThemeToken(theme, bg),
        borderRadius: borderRadius === "full" ? 9999 : borderRadius,
        width,
        height,

        borderColor: resolveThemeToken(theme, borderColor),
        borderWidth: borderWidth ? `${borderWidth}px` : undefined,
        borderStyle: borderWidth ? "solid" : undefined,

        ...(style as React.CSSProperties),
      }),
      [
        flexDirection,
        justifyContent,
        alignItems,
        flexWrap,
        flex,
        gap,
        m,
        mt,
        mr,
        mb,
        ml,
        mx,
        my,
        p,
        pt,
        pr,
        pb,
        pl,
        px,
        py,
        bg,
        borderRadius,
        width,
        height,
        borderColor,
        borderWidth,
        style,
        theme,
      ],
    );

    // Filter out undefined values to prevent React/JSDOM issues with shorthands
    const cleanStyles = React.useMemo(
      () =>
        Object.fromEntries(
          Object.entries(styles).filter(([_, v]) => v !== undefined),
        ),
      [styles],
    );

    return (
      <Component
        ref={ref}
        className={className}
        style={cleanStyles}
        disabled={disabled}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

Box.displayName = "Box";
